import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { WMEditableTitle, WMTooltip } from '@walkme/wm-ui';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cc from 'classcat';

import { useSelector } from '../../../../infrastructure/store.interface';
import { notificationPropertyChanged } from '../../../../state/actions/editNotification';
import { enqueueSnackbar } from '../../../../state/actions/ui';
import StepKeys from '../../../../models/step-keys';
import classes from './EditNotificationTitle.module.scss';
import { Controller, useFormContext } from 'react-hook-form';

const keyPrefix = 'components.editNotification.editNotificationHeader.editNotificationTitle';

interface IEditNotificationTitleProps {
    step: StepKeys;
    isNewNotification: boolean;
}

export const MAX_NOTIFICATION_TITLE_LENGTH = 100;
const NOTIFICATION_TITLE_LABEL = 'notification-title';

export const EditNotificationTitle = ({ step, isNewNotification }: IEditNotificationTitleProps) => {
    const { t } = useTranslation('', { keyPrefix });
    const dispatch = useDispatch();
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'title' does not exist on type 'Notificat... Remove this comment to see the full error message
    const { title } = useSelector(state => state.edit.notification);
    const [isFocused, setIsFocused] = useState(false);
    const [isDefaultTitle, setIsDefaultTitle] = useState(isNewNotification);
    const { clearErrors, setValue } = useFormContext();

    const debouncedSnackbar = useDebouncedCallback(() => {
        dispatch(enqueueSnackbar({ message: t('snackbar'), options: { variant: 'error' } }));
    }, 300);

    const UNTITLED_NOTIFICATION = useMemo(() => t('untitledNotification'), [t]);
    const previousUntitledRef = useRef(UNTITLED_NOTIFICATION);

    const checkDefaultTitle = useCallback(
        (title: string) => {
            setIsDefaultTitle(title === UNTITLED_NOTIFICATION);
        },
        [UNTITLED_NOTIFICATION]
    );

    useEffect(() => {
        checkDefaultTitle(title);
    }, [checkDefaultTitle, title]);

    useEffect(() => {
        if (!title) return;
        setValue(NOTIFICATION_TITLE_LABEL, title);
    }, [setValue, title]);

    const handleApprove = useCallback(
        (value: string) => {
            setIsFocused(false);
            dispatch(notificationPropertyChanged({ title: value }));
        },
        [dispatch]
    );

    const handleValidation = useCallback(
        (value: string) => {
            if (value && value.length === MAX_NOTIFICATION_TITLE_LENGTH) {
                debouncedSnackbar();
            }

            return !(value === '' || value === UNTITLED_NOTIFICATION);
        },
        [UNTITLED_NOTIFICATION, debouncedSnackbar]
    );

    useEffect(() => {
        if (title === previousUntitledRef.current) {
            dispatch(notificationPropertyChanged({ title: UNTITLED_NOTIFICATION }));
        }
        previousUntitledRef.current = UNTITLED_NOTIFICATION;
    }, [title, previousUntitledRef, UNTITLED_NOTIFICATION, dispatch]);

    return (
        <>
            {step === StepKeys.Themes && (
                <div className={classes.themeTitle} data-testid="notification-title-theme">
                    {isNewNotification ? t('newTitle') : t('editTitle')}
                </div>
            )}
            {step !== StepKeys.Themes && (
                <WMTooltip className={classes.tooltip} title={isFocused ? '' : t('rename')} placement="bottom" TransitionProps={{ exit: false }}>
                    {/*
                        This div is to allow tooltip - EditableTitle can't forward ref (will be added in the future).
                    */}
                    <div className={classes.editableTitleWrapper}>
                        <Controller
                            name={NOTIFICATION_TITLE_LABEL}
                            defaultValue={title ?? (UNTITLED_NOTIFICATION || '')}
                            rules={{ validate: value => handleValidation(value) }}
                            render={({ field: { value, onChange }, fieldState: { invalid } }) => {
                                return (
                                    <>
                                        <WMEditableTitle
                                            testKey="edit-notification-title"
                                            className={cc([classes.editableTitle, { [classes.active]: isDefaultTitle }])}
                                            value={value}
                                            maxLength={MAX_NOTIFICATION_TITLE_LENGTH}
                                            onEditStart={() => {
                                                clearErrors(NOTIFICATION_TITLE_LABEL);
                                                setIsFocused(true);
                                                setIsDefaultTitle(false);
                                            }}
                                            onEditEnd={() => {
                                                setIsFocused(false);
                                                checkDefaultTitle(value);
                                            }}
                                            onApprove={_value => {
                                                clearErrors(NOTIFICATION_TITLE_LABEL);
                                                handleApprove(_value);
                                                onChange(_value);
                                            }}
                                            hasError={invalid && step === StepKeys.Send}
                                        />
                                        {invalid && step === StepKeys.Send && <p className={classes.headerTitle}>{t('pleaseEnter')}</p>}
                                    </>
                                );
                            }}
                        />
                    </div>
                </WMTooltip>
            )}
        </>
    );
};
